import React, { useEffect, useMemo, useState } from 'react';
import ClinicStyle from './Clinic.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import DeleteDialog from '../../components/DeleteDialog';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { toast } from 'react-toastify';
import axios from 'axios';
import { ApiHeaderWithToken } from '../../common/apisHeaders.js';
import { getClinics, addClinic, updateClinic, deleteClinic } from '../../apis/clinicSlice';

// const UploadField = ({ label, id, preview, onChange }) => (
//   <Card variant="outlined">
//     <CardContent>
//       <Typography variant="subtitle2" gutterBottom>
//         {label}
//       </Typography>

//       <Button component="label" variant="outlined" fullWidth size="small">
//         Upload {label}
//         <input hidden accept="image/*" type="file" onChange={onChange} />
//       </Button>

//       {preview && (
//         <Box
//           mt={1}
//           sx={{
//             border: '1px solid #ddd',
//             borderRadius: 1,
//             p: 1,
//             display: 'flex',
//             justifyContent: 'center',
//           }}
//         >
//           <img
//             src={preview}
//             alt={label}
//             style={{
//               maxWidth: '100%',
//               maxHeight: 70,
//               objectFit: 'contain',
//             }}
//           />
//         </Box>
//       )}
//     </CardContent>
//   </Card>
// );
const UploadField = ({ label, preview, onChange, error, required = false }) => (
  <Card
    variant="outlined"
    sx={{
      borderColor: error ? 'error.main' : '#dbe5ef',
      borderRadius: 1,
    }}
  >
    <CardContent sx={{ py: 1, px: 1.15, '&:last-child': { pb: 1 } }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: 'var(--color-heading)', display: 'block', mb: 0.35 }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.35 }}>
            *
          </Box>
        )}
      </Typography>

      <Button
        component="label"
        variant="outlined"
        fullWidth
        size="small"
        sx={{ py: 0.15, fontSize: '0.72rem', textTransform: 'none' }}
      >
        Choose file
        <input hidden accept="image/*" type="file" onChange={onChange} />
      </Button>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.35 }}>
          {error}
        </Typography>
      )}

      {preview && (
        <Box
          mt={0.75}
          sx={{
            borderRadius: 1,
            p: 0.75,
            display: 'flex',
            justifyContent: 'center',
            border: '1px solid var(--color-border-soft)',
            backgroundColor: 'var(--color-bg-soft)',
          }}
        >
          <img
            src={preview}
            alt={label}
            style={{
              maxWidth: '100%',
              maxHeight: 52,
              objectFit: 'contain',
            }}
          />
        </Box>
      )}
    </CardContent>
  </Card>
);

export default function Clinic() {
  const dispatch = useDispatch();

  // delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const { clinics, loading, totalCount } = useSelector(
    (state) => state.clinicData || { clinics: [], loading: false, totalCount: 0 },
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [openPopup, setOpenPopup] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [editId, setEditId] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [errors, setErrors] = useState({
    email: '',
    phone: '',
  });

  useEffect(() => {
    dispatch(getClinics({ page, pageSize }));
  }, [dispatch, page, pageSize]);

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleOpenAdd = () => {
    setEditId(null);
    setForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      logoBase64: null,
      stampBase64: null,
      signatureBase64: null,
    });
    setLogoPreview(null);
    setStampPreview(null);
    setSignaturePreview(null);
    setOpenPopup(true);
    setErrors({ email: '', phone: '' });
  };

  const handleOpenEdit = (row) => {
    setEditId(row._id);
    setForm({
      name: row.name || '',
      email: row.email || '',
      phone: row.phone || '',
      address: row.address || '',
      logoBase64: null,
      stampBase64: null,
    });
    setLogoPreview(row.logo || null);
    setStampPreview(row.stamp || null);
    setSignaturePreview(row.signature || null);
    setOpenPopup(true);
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const phoneRegex = /^[6-9]{1}[0-9]{9}$/;

  const handleChange = (field, value) => {
    let newValue = value;
    let errorMsg = '';

    if (field === 'phone') {
      newValue = value.replace(/\D/g, ''); // allow only digits

      if (newValue.length > 10) return;

      // Validate first digit (6–9)
      if (newValue.length >= 1 && !/^[6-9]/.test(newValue)) {
        errorMsg = 'Mobile number must start with 6, 7, 8, or 9';
      }
      // Validate full number when 10 digits entered
      else if (newValue.length === 10 && !phoneRegex.test(newValue)) {
        errorMsg = 'Enter valid 10-digit mobile number';
      }
      // Optional: show helper message while typing
      else if (newValue.length > 0 && newValue.length < 10) {
        errorMsg = 'Mobile number must be exactly 10 digits';
      }
    }

    // 👉 EMAIL validation
    if (field === 'email') {
      if (newValue && !emailRegex.test(newValue)) {
        errorMsg = 'Enter valid email address';
      }
    }

    setForm((prev) => ({ ...prev, [field]: newValue }));
    setErrors((prev) => ({ ...prev, [field]: errorMsg }));
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      // request presigned URL from backend
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_API}/clinic/presign`,
        { fileName: file.name, contentType: file.type, folder: 'clinics' },
        ApiHeaderWithToken(),
      );

      const { url, publicUrl } = resp.data.data || {};
      if (!url) throw new Error('No presigned url returned');

      // upload directly to S3
      await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      // set preview and form to use publicUrl
      const previewUrl = URL.createObjectURL(file);
      if (type === 'logo') {
        setLogoPreview(previewUrl);
        setForm((f) => ({ ...f, logo: publicUrl }));
      } else if (type === 'stamp') {
        setStampPreview(previewUrl);
        setForm((f) => ({ ...f, stamp: publicUrl }));
      } else if (type === 'signature') {
        setSignaturePreview(previewUrl);
        setForm((f) => ({ ...f, signature: publicUrl }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload file');
    }
  };

  // const handleSubmit = async () => {
  //   if (!form.name.trim()) {
  //     toast.error('Name is required');
  //     return;
  //   }

  //    if (form.email && !emailRegex.test(form.email)) {
  //   toast.error('Invalid email');
  //   return;
  // }

  // if (form.phone && !phoneRegex.test(form.phone)) {
  //   toast.error('Phone must be 10 digits');
  //   return;
  // }

  //   let response;

  //   if (editId) {
  //     response = await dispatch(updateClinic({ id: editId, ...form }));
  //   } else {
  //     response = await dispatch(addClinic(form));
  //   }

  //   if (response?.payload?.message) {
  //     toast.success(response.payload.message);
  //     setOpenPopup(false);
  //     dispatch(getClinics({ page, pageSize }));
  //   } else {
  //     toast.error(response?.payload?.message || 'Error');
  //   }
  // };
  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    if (!form.phone.trim()) newErrors.phone = 'Mobile number is required';
    if (!form.address.trim()) newErrors.address = 'Address is required';

    if (!logoPreview) newErrors.logo = 'Logo is required';
    if (!stampPreview) newErrors.stamp = 'Stamp is required';
    if (!signaturePreview) newErrors.signature = 'Signature is required';

    if (form.email && !emailRegex.test(form.email)) newErrors.email = 'Enter valid email';

    if (form.phone && !phoneRegex.test(form.phone))
      newErrors.phone = 'Enter valid mobile (start with 6–9)';

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fill all required fields');
      return;
    }

    let response;

    if (editId) {
      response = await dispatch(updateClinic({ id: editId, ...form }));
    } else {
      response = await dispatch(addClinic(form));
    }

    if (response?.payload?.message) {
      toast.success(response.payload.message);
      setOpenPopup(false);
      dispatch(getClinics({ page, pageSize }));
    } else {
      toast.error(response?.payload?.message || 'Error');
    }
  };

  // perform actual deletion (called after confirmation)
  const handleDelete = async (row) => {
    const response = await dispatch(deleteClinic(row._id));

    if (response?.payload?.message) {
      toast.success(response.payload.message);
      dispatch(getClinics({ page, pageSize }));
    } else {
      toast.error('Error deleting clinic');
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    await handleDelete(deleteRow);
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const columnDefs = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1.1, minWidth: 110 },
      { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 130 },
      { field: 'phone', headerName: 'Phone', flex: 0, width: 108, minWidth: 108, maxWidth: 120 },
      { field: 'address', headerName: 'Address', flex: 1.4, minWidth: 140 },
      {
        colId: 'actions',
        headerName: 'Action',
        width: 96,
        flex: 0,
        minWidth: 96,
        maxWidth: 96,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params) => {
          const ctx = params.context || {};
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 0.25,
                justifyContent: 'flex-end',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Tooltip title="Edit">
                <IconButton size="small" color="primary" onClick={() => ctx.onEdit?.(params.data)}>
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => ctx.onDelete?.(params.data)}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [],
  );

  const gridContext = useMemo(
    () => ({
      onEdit: handleOpenEdit,
      onDelete: handleDeleteClick,
    }),
    [handleOpenEdit, handleDeleteClick],
  );

  const isDisabled =
    !form.name.trim() ||
    !form.email.trim() ||
    !form.phone.trim() ||
    !form.address.trim() ||
    !logoPreview ||
    !stampPreview ||
    !signaturePreview ||
    !!errors.email ||
    !!errors.phone;

  return (
    <div className={ClinicStyle.page}>
      <DeleteDialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        compact
      />
      <Card className={ClinicStyle.tableCard}>
        <div className={ClinicStyle.tableHeader}>
          <h2 className={ClinicStyle.tableTitle}>Clinics</h2>
          <Button
            className={ClinicStyle.addBtn}
            variant="contained"
            size="small"
            startIcon={<LocalHospitalIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenAdd}
          >
            Add Clinic
          </Button>
        </div>
        <CommonAgGrid
          rowData={clinics || []}
          columnDefs={columnDefs}
          context={gridContext}
          getRowId={(p) => String(p.data._id)}
          loading={loading}
          height={360}
          pagination
          paginationMode="server"
          rowCount={Number(totalCount) || 0}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[5, 10, 25]}
        />
      </Card>

      <Dialog
        open={openPopup}
        fullWidth
        maxWidth="sm"
        onClose={() => setOpenPopup(false)}
        PaperProps={{ sx: { borderRadius: 2, border: '1px solid var(--color-border)' } }}
      >
        <DialogTitle
          sx={{ py: 1.1, px: 2, fontSize: '1rem', fontWeight: 800, color: 'var(--color-heading)' }}
        >
          {editId ? 'Edit clinic' : 'Add clinic'}
        </DialogTitle>

        <DialogContent sx={{ px: 2, pt: 0, pb: 1 }}>
          {/* <TextField autoFocus margin="dense" label="Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField margin="dense" label="Email" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <TextField margin="dense" label="Phone" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <TextField margin="dense" label="Address" fullWidth value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /> */}
          {/* <div style={{ display: 'flex', gap: 16, marginTop: 12, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                accept="image/*"
                style={{ display: 'block' }}
                id="logo-upload"
                type="file"
                onChange={(e) => handleFileChange(e, 'logo')}
              />
              <label htmlFor="logo-upload">Logo</label>
              {logoPreview ? (
                <img src={logoPreview} alt="logo" style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #ccc' }} />
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                accept="image/*"
                style={{ display: 'block' }}
                id="stamp-upload"
                type="file"
                onChange={(e) => handleFileChange(e, 'stamp')}
              />
              <label htmlFor="stamp-upload">Stamp</label>
              {stampPreview ? (
                <img src={stampPreview} alt="stamp" style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #ccc' }} />
              ) : null}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                accept="image/*"
                style={{ display: 'block' }}
                id="signature-upload"
                type="file"
                onChange={(e) => handleFileChange(e, 'signature')}
              />
              <label htmlFor="signature-upload">Signature</label>
              {signaturePreview ? (
                <img src={signaturePreview} alt="signature" style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #ccc' }} />
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                accept="image/*"
                style={{ display: 'block' }}
                id="watermark-upload"
                type="file"
                onChange={(e) => handleFileChange(e, 'watermark')}
              />
              <label htmlFor="watermark-upload">Watermark</label>
              {watermarkPreview ? (
                <img src={watermarkPreview} alt="watermark" style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #ccc' }} />
              ) : null}
            </div>
          </div> */}
          <Grid container spacing={1.25} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                required
                size="small"
                margin="dense"
                label="Name"
                fullWidth
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* <TextField
            label="Email"
            fullWidth
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          /> */}
              <TextField
                label="Email"
                fullWidth
                required
                size="small"
                margin="dense"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                FormHelperTextProps={{ sx: { m: 0, mt: 0.25 } }}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              {/* <TextField
            label="Phone"
            fullWidth
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          /> */}
              <TextField
                label="Mobile Number"
                required
                fullWidth
                size="small"
                margin="dense"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                FormHelperTextProps={{ sx: { m: 0, mt: 0.25 } }}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 10,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                required
                label="Address"
                fullWidth
                size="small"
                margin="dense"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-text-muted)' }}>
                Upload assets
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <UploadField
                required
                label="Logo"
                id="logo-upload"
                preview={logoPreview}
                onChange={(e) => handleFileChange(e, 'logo')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <UploadField
                required
                label="Stamp"
                id="stamp-upload"
                preview={stampPreview}
                onChange={(e) => handleFileChange(e, 'stamp')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <UploadField
                required
                label="Signature"
                id="signature-upload"
                preview={signaturePreview}
                onChange={(e) => handleFileChange(e, 'signature')}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, gap: 1 }}>
          <Button
            onClick={() => setOpenPopup(false)}
            variant="outlined"
            size="small"
            color="inherit"
            sx={{ textTransform: 'none', borderColor: 'var(--color-border)' }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={isDisabled}
            className={isDisabled ? 'dialogDisableBtn' : 'dialogSubmitBtn'}
            sx={{ textTransform: 'none', minWidth: 96 }}
          >
            {editId ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
